import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { notification } from "antd";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import { normalizeLanguage, translatePair } from "../../services/language";
import ModernSidebar, { useModernRouteActions } from "../../components/lobby/ModernSidebar";
import "../../components/lobby/lobbyModern.css";
import "./newsV2.css";

// Página Noticias (diseño 1, «portada», elegido por Yosdel el 2026-10-18). Todo sale de /news:
// noticias del equipo y automáticas (puzzles y torneos nuevos), campeonato, «En qué trabajamos»,
// calendario y el foro de la Comunidad.

export const NEWS_NOTIFY_STORAGE_KEY = "xima_news_notify_v1";
const TABS = ["news", "community", "roadmap", "calendar"];

const CATEGORIES = {
  novedad: { es: "Novedad", en: "New", icon: "✨", tone: "cyan" },
  puzzle: { es: "Puzzle nuevo", en: "New puzzle", icon: "🧩", tone: "green" },
  torneo: { es: "Torneo", en: "Tournament", icon: "🏆", tone: "gold" },
  evento: { es: "Evento", en: "Event", icon: "🎉", tone: "pink" },
  mejora: { es: "Mejora", en: "Improvement", icon: "🔧", tone: "violet" },
  campeonato: { es: "Campeonato", en: "Championship", icon: "🥇", tone: "gold" },
};
const FILTERS = [
  { id: "", es: "Todas", en: "All" },
  { id: "novedad", es: "✨ Novedades", en: "✨ News" },
  { id: "puzzle", es: "🧩 Puzzles", en: "🧩 Puzzles" },
  { id: "torneo", es: "🏆 Torneos", en: "🏆 Tournaments" },
  { id: "evento", es: "🎉 Eventos", en: "🎉 Events" },
  { id: "mejora", es: "🔧 Mejoras", en: "🔧 Improvements" },
];
const FORUM_CATEGORIES = [
  { id: "", icon: "🗂️", es: "Todos los temas", en: "All topics" },
  { id: "estrategia", icon: "♟️", es: "Estrategia", en: "Strategy" },
  { id: "ideas", icon: "💡", es: "Ideas y sugerencias", en: "Ideas" },
  { id: "problemas", icon: "🐞", es: "Problemas", en: "Problems" },
  { id: "torneos", icon: "🏆", es: "Torneos", en: "Tournaments" },
  { id: "general", icon: "☕", es: "Charla general", en: "General chat" },
];
const ROADMAP_COLUMNS = [
  { id: "doing", es: "EN CURSO", en: "IN PROGRESS", tone: "cyan" },
  { id: "next", es: "PRÓXIMO", en: "NEXT", tone: "gold" },
  { id: "done", es: "HECHO", en: "DONE", tone: "green" },
];
const LINK_PRESETS = [
  { link: "", es: "Sin botón", en: "No button" },
  { link: "/game/championship", label: "Inscribirme", es: "Campeonato", en: "Championship" },
  { link: "/game/puzzles", label: "Resolver", es: "Puzzles", en: "Puzzles" },
  { link: "/game/play", label: "Ver torneos", es: "Torneos", en: "Tournaments" },
  { link: "/game/lobby", label: "Jugar", es: "Jugar", en: "Play" },
  { link: "/game/store", label: "Ir a la tienda", es: "Tienda", en: "Store" },
  { link: "/game/premium", label: "Ver Premium", es: "Premium", en: "Premium" },
  { link: "/game/friends", label: "Ir a Amigos", es: "Amigos", en: "Friends" },
];
// Dibujo de cada noticia según de qué habla (o el que elija el admin al publicar).
const ICON_RULES = [
  [/tarjeta[s]? de regalo|targeta[s]? de regalo|gift ?card|amazon/i, "🎁"],
  [/billetera|villetera|cartera|wallet/i, "👛"],
  [/reconex|reconect|conexi[oó]n|desconect/i, "🔌"],
  [/soporte|suport|support|ayuda/i, "🛟"],
  [/premium|paypal|suscrip/i, "💎"],
  [/tienda|ofertas|store/i, "🛍️"],
  [/amigo|invita|friend/i, "👥"],
  [/aprender|gu[ií]a|tutorial|libro/i, "📖"],
  [/estad[ií]stica/i, "📊"],
  [/ver partidos|en vivo|espectador|repetici/i, "📺"],
  [/ajustes|configuraci/i, "⚙️"],
  [/historial/i, "🕘"],
  [/comunidad|foro|noticias/i, "📰"],
  [/bono|moneda|coin|premio/i, "🪙"],
  [/bot\b|bots\b|inteligencia artificial|\bia\b/i, "🤖"],
  [/campeonato/i, "🥇"],
  [/torneo/i, "🏆"],
  [/puzzle/i, "🧩"],
  [/tablero|bloque|dise[ñn]/i, "🎨"],
  [/m[oó]vil|android|iphone|app\b/i, "📱"],
  [/video|youtube/i, "🎬"],
];
const ICON_CHOICES = ["", "🎁", "👛", "🔌", "🛟", "💎", "🛍️", "👥", "📖", "📊", "📺", "⚙️", "🕘", "📰", "🪙", "🤖", "🥇", "🏆", "🧩", "🎨", "📱", "🎬", "🎉", "✨", "🔧", "📣", "🚀", "🛠️", "❤️"];
const iconFor = (item) => {
  if (item?.icon) return item.icon;
  const meta = CATEGORIES[item?.category] || CATEGORIES.novedad;
  if (item?.kind === "auto") return meta.icon;
  const title = String(item?.title || "");
  const text = `${title} ${item?.text || ""}`;
  const hit = ICON_RULES.find(([rule]) => rule.test(title)) || ICON_RULES.find(([rule]) => rule.test(text));
  return hit ? hit[1] : meta.icon;
};

// Con «Todas» van en tres columnas de color: novedades a la izquierda, mejoras en el centro y
// puzzles a la derecha (si una columna se queda sin noticias, la rellenan las otras).
const columnOf = (item) => (item.category === "mejora" ? 1 : item.category === "puzzle" ? 2 : 0);
const splitInColumns = (items) => {
  const columns = [[], [], []];
  items.forEach((item) => columns[columnOf(item)].push(item));
  const rows = Math.ceil(items.length / 3);
  const extra = [];
  columns.forEach((column) => extra.push(...column.splice(rows)));
  extra.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  columns.forEach((column) => {
    while (column.length < rows && extra.length) column.push(extra.shift());
  });
  return columns;
};

const MODE_NAMES = { ciencia: "Ciencia", azar: "Azar", cienzar: "Cienzar", contrazar: "Contrazar" };
const AVATAR_TONES = ["#1e3a8a", "#6b21a8", "#0f766e", "#9d174d", "#7c5a12", "#155e75"];

const readNotify = () => {
  try {
    return window.localStorage.getItem(NEWS_NOTIFY_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
};

const toneOf = (name) => {
  const text = String(name || "?");
  let sum = 0;
  for (let index = 0; index < text.length; index += 1) sum += text.charCodeAt(index);
  return AVATAR_TONES[sum % AVATAR_TONES.length];
};

const initialOf = (name) => String(name || "?").trim().charAt(0).toUpperCase() || "?";

const errorText = (error, fallback) => error?.response?.data?.message || fallback;

const Avatar = ({ name, small }) => (
  <span className={`xnw-av${small ? " is-small" : ""}`} style={{ background: toneOf(name) }}>
    {initialOf(name)}
  </span>
);

const Modal = ({ onClose, wide, children }) => {
  useEffect(() => {
    const onKey = (event) => event.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
  <div className="xnw-overlay" role="dialog" aria-modal="true" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <div className={`xnw-modal${wide ? " is-wide" : ""}`}>
      <button type="button" className="xnw-modal-close" onClick={onClose} aria-label="Cerrar">
        ✕
      </button>
      {children}
    </div>
  </div>
  );
};


const ItemDetail = ({ ctx, item}) => {
  const { t, locale, timeAgo, patchItem, loadFeed, setOpenItemKey, setEditor, goTo, likeItem, isAdmin, autoText, renderCategoryPill } = ctx;
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState("");
  const target = item.key;

  const loadComments = useCallback(async () => {
    try {
      const response = await axios.get("/news/comments", { params: { target } });
      setComments(response?.data?.comments || []);
    } catch {
      setComments([]);
    }
  }, [target]);

  useEffect(() => {
    loadComments();
  }, [loadComments]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const response = await axios.post("/news/comments", { target, text });
      setComments((current) => [...(current || []), response.data.comment]);
      patchItem(item.key, { comments: (item.comments || 0) + 1 });
      setText("");
    } catch (error) {
      notification.error({ message: t("Comentario", "Comment"), description: errorText(error, t("No se pudo enviar.", "Could not send.")) });
    } finally {
      setSending(false);
    }
  };

  const removeComment = async (comment) => {
    if (!window.confirm(t("¿Borrar este comentario?", "Delete this comment?"))) return;
    try {
      await axios.delete(`/news/comments/${comment.id}`);
      setComments((current) => (current || []).filter((entry) => entry.id !== comment.id));
      patchItem(item.key, { comments: Math.max(0, (item.comments || 0) - 1) });
    } catch (error) {
      notification.error({ message: t("Comentario", "Comment"), description: errorText(error, t("No se pudo borrar.", "Could not delete.")) });
    }
  };

  const removePost = async () => {
    if (!window.confirm(t("¿Borrar esta noticia para todos?", "Delete this post for everyone?"))) return;
    try {
      await axios.delete(`/news/posts/${encodeURIComponent(item.id)}`);
      setOpenItemKey("");
      loadFeed();
    } catch (error) {
      notification.error({ message: t("Noticias", "News"), description: errorText(error, t("No se pudo borrar.", "Could not delete.")) });
    }
  };

  const togglePin = async () => {
    try {
      await axios.patch(`/news/posts/${encodeURIComponent(item.id)}`, { pinned: !item.pinned });
      loadFeed();
    } catch (error) {
      notification.error({ message: t("Noticias", "News"), description: errorText(error, t("No se pudo guardar.", "Could not save.")) });
    }
  };

  return (
    <Modal onClose={() => setOpenItemKey("")} wide>
      <div className="xnw-detail">
        <div className="xnw-detail-pills">
          {renderCategoryPill(item.category)}
          {item.pinned ? <span className="xnw-pill is-red">📌 {t("FIJADA", "PINNED")}</span> : null}
        </div>
        <h2>{item.title}</h2>
        <div className="xnw-muted">
          {item.author} · {new Date(item.createdAt).toLocaleString(locale, { dateStyle: "long", timeStyle: "short" })}
        </div>
        {autoText(item) ? <div className="xnw-detail-text">{autoText(item)}</div> : null}
        <div className="xnw-actions">
          {item.ctaLink ? (
            <button type="button" className="xnw-btn is-go" onClick={() => goTo(item.ctaLink)}>
              {item.ctaLabel || t("Ver", "Open")}
            </button>
          ) : null}
          <button type="button" className={`xnw-btn${item.liked ? " is-liked" : ""}`} onClick={() => likeItem(item)}>
            👍 {item.liked ? t("Te gusta", "Liked") : t("Me gusta", "Like")} · {item.likes}
          </button>
          {isAdmin && item.kind === "post" ? (
            <>
              <button type="button" className="xnw-btn" onClick={() => setEditor({ ...item })}>
                ✏️ {t("Editar", "Edit")}
              </button>
              <button type="button" className="xnw-btn" onClick={togglePin}>
                📌 {item.pinned ? t("Quitar de destacada", "Unpin") : t("Poner destacada", "Pin")}
              </button>
              <button type="button" className="xnw-btn is-red" onClick={removePost}>
                🗑 {t("Borrar", "Delete")}
              </button>
            </>
          ) : null}
        </div>
        <div className="xnw-comments">
          <div className="xnw-ct">💬 {t("Comentarios", "Comments")} ({comments ? comments.length : item.comments})</div>
          {comments === null ? (
            <div className="xnw-muted">{t("Cargando…", "Loading…")}</div>
          ) : comments.length ? (
            comments.map((comment) => (
              <div key={comment.id} className="xnw-comment">
                <Avatar name={comment.authorName} small />
                <div className="xnw-bubble">
                  <b className={comment.authorIsAdmin ? "is-admin" : ""}>
                    {comment.authorName}
                    {comment.authorIsAdmin ? " (Admin)" : ""}
                  </b>
                  {editingId === comment.id ? (
                    <CommentEditor
                      ctx={ctx}
                      comment={comment}
                      onCancel={() => setEditingId("")}
                      onSaved={(saved) => {
                        setEditingId("");
                        setComments((current) => (current || []).map((entry) => (entry.id === saved.id ? { ...entry, text: saved.text, editedAt: saved.editedAt } : entry)));
                      }}
                    />
                  ) : (
                    <div>{comment.text}</div>
                  )}
                  <small>
                    {timeAgo(comment.createdAt)}
                    {comment.editedAt ? ` · ${t("editado", "edited")}` : ""}
                    {comment.canEdit && editingId !== comment.id ? (
                      <button type="button" className="xnw-link" onClick={() => setEditingId(comment.id)}>
                        {t("Editar", "Edit")}
                      </button>
                    ) : null}
                    {comment.canDelete ? (
                      <button type="button" className="xnw-link is-red" onClick={() => removeComment(comment)}>
                        {t("Borrar", "Delete")}
                      </button>
                    ) : null}
                  </small>
                </div>
              </div>
            ))
          ) : (
            <div className="xnw-muted">{t("Sé el primero en comentar.", "Be the first to comment.")}</div>
          )}
          <div className="xnw-compose">
            <textarea
              value={text}
              maxLength={2000}
              onChange={(event) => setText(event.target.value)}
              placeholder={t("Escribe un comentario…", "Write a comment…")}
            />
            <button type="button" className="xnw-btn is-go" disabled={!text.trim() || sending} onClick={send}>
              {t("Enviar ➤", "Send ➤")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const PostEditor = ({ ctx, initial}) => {
  const { t, loadFeed, setOpenItemKey, setEditor } = ctx;
  const [form, setForm] = useState(() => ({
    title: initial?.title || "",
    text: initial?.text || "",
    category: initial?.category || "novedad",
    pinned: Boolean(initial?.pinned),
    icon: initial?.icon || "",
    ctaLink: initial?.ctaLink || "",
    ctaLabel: initial?.ctaLabel || "",
  }));
  const [saving, setSaving] = useState(false);
  const isPreset = LINK_PRESETS.some((preset) => preset.link === form.ctaLink);
  const [customLink, setCustomLink] = useState(!isPreset);
  const set = (field) => (event) =>
    setForm((current) => ({ ...current, [field]: event.target.type === "checkbox" ? event.target.checked : event.target.value }));

  const save = async () => {
    if (!form.title.trim() || saving) return;
    setSaving(true);
    try {
      if (initial?.id) {
        await axios.patch(`/news/posts/${encodeURIComponent(initial.id)}`, form);
      } else {
        await axios.post("/news/posts", form);
      }
      setEditor(null);
      setOpenItemKey("");
      notification.success({ message: initial?.id ? t("Noticia guardada", "Post saved") : t("Noticia publicada", "Post published") });
      loadFeed();
    } catch (error) {
      notification.error({ message: t("Noticias", "News"), description: errorText(error, t("No se pudo guardar.", "Could not save.")) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal onClose={() => setEditor(null)}>
      <div className="xnw-form">
        <h2>{initial?.id ? t("Editar noticia", "Edit post") : t("Publicar noticia", "Publish news")}</h2>
        <label>
          {t("Título", "Title")}
          <input value={form.title} maxLength={140} onChange={set("title")} placeholder={t("Ej.: 15 puzzles nuevos de Ciencia", "E.g. 15 new Science puzzles")} />
        </label>
        <label>
          {t("Tipo", "Type")}
          <select value={form.category} onChange={set("category")}>
            {Object.entries(CATEGORIES).map(([id, meta]) => (
              <option key={id} value={id}>
                {meta.icon} {t(meta.es, meta.en)}
              </option>
            ))}
          </select>
        </label>
        <div className="xnw-icon-pick">
          <span>
            {t("Dibujo", "Picture")} <small>{t("(Automático lo elige según el título)", "(Auto picks it from the title)")}</small>
          </span>
          <div>
            {ICON_CHOICES.map((icon) => (
              <button
                key={icon || "auto"}
                type="button"
                className={`xnw-icon-opt${form.icon === icon ? " is-on" : ""}`}
                onClick={() => setForm((current) => ({ ...current, icon }))}
              >
                {icon || t("Auto", "Auto")}
              </button>
            ))}
          </div>
          <div className="xnw-icon-preview">
            {iconFor({ ...form, kind: "post" })}
          </div>
        </div>
        <label>
          {t("Texto", "Text")}
          <textarea value={form.text} maxLength={5000} rows={6} onChange={set("text")} placeholder={t("Cuenta la noticia…", "Tell the story…")} />
        </label>
        <label>
          {t("Botón de la noticia", "Post button")}
          <select
            value={customLink ? "__custom" : form.ctaLink}
            onChange={(event) => {
              if (event.target.value === "__custom") {
                setCustomLink(true);
                return;
              }
              const preset = LINK_PRESETS.find((entry) => entry.link === event.target.value);
              setCustomLink(false);
              setForm((current) => ({ ...current, ctaLink: preset?.link || "", ctaLabel: preset?.label || "" }));
            }}
          >
            {LINK_PRESETS.map((preset) => (
              <option key={preset.link || "none"} value={preset.link}>
                {t(preset.es, preset.en)}
              </option>
            ))}
            <option value="__custom">{t("Otro enlace…", "Other link…")}</option>
          </select>
        </label>
        {customLink ? (
          <label>
            {t("Enlace (empieza por / o https://)", "Link (starts with / or https://)")}
            <input value={form.ctaLink} maxLength={200} onChange={set("ctaLink")} placeholder="/game/championship" />
          </label>
        ) : null}
        {form.ctaLink ? (
          <label>
            {t("Texto del botón", "Button text")}
            <input value={form.ctaLabel} maxLength={40} onChange={set("ctaLabel")} />
          </label>
        ) : null}
        <label className="xnw-check">
          <input type="checkbox" checked={form.pinned} onChange={set("pinned")} />
          {t("Poner como noticia destacada (grande, arriba)", "Feature this post (big, on top)")}
        </label>
        <div className="xnw-actions">
          <button type="button" className="xnw-btn" onClick={() => setEditor(null)}>
            {t("Cancelar", "Cancel")}
          </button>
          <button type="button" className="xnw-btn is-go" disabled={!form.title.trim() || saving} onClick={save}>
            {initial?.id ? t("Guardar", "Save") : t("Publicar", "Publish")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const Community = ({ ctx }) => {
  const { t, timeAgo, location, setTab } = ctx;
  const params = new URLSearchParams(location.search);
  const openTopicId = params.get("topic") || "";
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [forum, setForum] = useState(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [composer, setComposer] = useState(null);

  const loadTopics = useCallback(async () => {
    try {
      const response = await axios.get("/news/forum/topics", { params: { category, q: search || undefined } });
      setForum(response.data);
    } catch (error) {
      setForum({ topics: [], counts: {}, error: errorText(error, t("No se pudieron cargar los temas.", "Could not load topics.")) });
    }
  }, [category, search, t]);

  useEffect(() => {
    const timer = window.setTimeout(loadTopics, search ? 300 : 0);
    return () => window.clearTimeout(timer);
  }, [loadTopics, search]);

  const openTopic = (id) => setTab("community", id ? `&topic=${id}` : "");

  if (openTopicId) {
    return <TopicView ctx={ctx} topicId={openTopicId} onBack={() => { openTopic(""); }} />;
  }

  return (
    <div className="xnw-forum">
      <div className="xnw-card xnw-cats">
        {FORUM_CATEGORIES.map((entry) => (
          <button
            key={entry.id || "all"}
            type="button"
            className={`xnw-cat${category === entry.id ? " is-on" : ""}`}
            onClick={() => setCategory(entry.id)}
          >
            {entry.icon} {t(entry.es, entry.en)}
            <span>{entry.id ? forum?.counts?.[entry.id] || 0 : forum?.counts?.total || 0}</span>
          </button>
        ))}
        <div className="xnw-muted xnw-rules">
          {t(
            "Reglas: respeto, nada de spam. Los administradores pueden fijar o borrar temas.",
            "Rules: be respectful, no spam. Admins can pin or delete topics."
          )}
        </div>
      </div>
      <div className="xnw-forum-main">
        <div className="xnw-card xnw-new">
          <input
            value={draftTitle}
            maxLength={140}
            onChange={(event) => setDraftTitle(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && setComposer({ title: draftTitle })}
            placeholder={t("✏️ Escribe el título de un tema nuevo…", "✏️ Write the title of a new topic…")}
          />
          <button type="button" className="xnw-btn is-go" onClick={() => setComposer({ title: draftTitle })}>
            ＋ {t("Nuevo tema", "New topic")}
          </button>
        </div>
        <div className="xnw-search">
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t("🔎 Buscar tema…", "🔎 Search topics…")} />
        </div>
        <div className="xnw-card xnw-topics">
          <div className="xnw-topic is-head">
            <span />
            <span>{t("TEMA", "TOPIC")}</span>
            <span>{t("RESPUESTAS", "REPLIES")}</span>
            <span>{t("ME GUSTA", "LIKES")}</span>
            <span>{t("ÚLTIMA ACTIVIDAD", "LAST ACTIVITY")}</span>
          </div>
          {forum === null ? (
            <div className="xnw-empty">{t("Cargando…", "Loading…")}</div>
          ) : forum.error ? (
            <div className="xnw-empty">{forum.error}</div>
          ) : forum.topics.length ? (
            forum.topics.map((topic) => (
              <button key={topic.id} type="button" className="xnw-topic" onClick={() => openTopic(topic.id)}>
                <Avatar name={topic.authorName} />
                <span className="xnw-topic-title">
                  <span>
                    {topic.pinned ? <span className="xnw-pill is-red">📌 {t("FIJADO", "PINNED")}</span> : null}
                    {topic.resolved ? <span className="xnw-pill is-green">✓ {t("RESUELTO", "SOLVED")}</span> : null}
                    <b>{topic.title}</b>
                  </span>
                  <small>
                    {topic.authorName}
                    {topic.authorIsAdmin ? " (Admin)" : ""} ·{" "}
                    {t(
                      FORUM_CATEGORIES.find((entry) => entry.id === topic.category)?.es || "Charla general",
                      FORUM_CATEGORIES.find((entry) => entry.id === topic.category)?.en || "General chat"
                    )}
                  </small>
                </span>
                <span className="xnw-num">
                  {topic.replyCount}
                  <small>{t("respuestas", "replies")}</small>
                </span>
                <span className="xnw-num">
                  {topic.likeCount}
                  <small>👍</small>
                </span>
                <span className="xnw-muted">{timeAgo(topic.lastActivityAt)}</span>
              </button>
            ))
          ) : (
            <div className="xnw-empty">
              {search || category
                ? t("No hay temas con ese filtro.", "No topics match.")
                : t("Todavía no hay temas. ¡Abre el primero!", "No topics yet. Start the first one!")}
            </div>
          )}
        </div>
      </div>
      {composer ? (
        <TopicComposer
        ctx={ctx}
          initialTitle={composer.title}
          initialCategory={category || "general"}
          onClose={() => setComposer(null)}
          onCreated={(topic) => {
            setComposer(null);
            setDraftTitle("");
            openTopic(topic.id);
          }}
        />
      ) : null}
    </div>
  );
};

// Editar un comentario o una respuesta en su sitio.
const CommentEditor = ({ ctx, comment, onSaved, onCancel }) => {
  const { t } = ctx;
  const [text, setText] = useState(comment.text || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!text.trim() || saving) return;
    setSaving(true);
    try {
      const response = await axios.patch(`/news/comments/${comment.id}`, { text });
      onSaved(response.data.comment);
    } catch (error) {
      notification.error({ message: t("Comentario", "Comment"), description: errorText(error, t("No se pudo guardar.", "Could not save.")) });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="xnw-inline-edit">
      <textarea value={text} maxLength={2000} onChange={(event) => setText(event.target.value)} autoFocus />
      <div className="xnw-actions">
        <button type="button" className="xnw-btn" onClick={onCancel}>
          {t("Cancelar", "Cancel")}
        </button>
        <button type="button" className="xnw-btn is-go" disabled={!text.trim() || saving} onClick={save}>
          {t("Guardar", "Save")}
        </button>
      </div>
    </div>
  );
};

const TopicComposer = ({ ctx, initialTitle, initialCategory, onClose, onCreated, editTopic }) => {
  const { t } = ctx;
  const [title, setTitle] = useState(editTopic?.title || initialTitle || "");
  const [body, setBody] = useState(editTopic?.body || "");
  const [category, setCategory] = useState(editTopic?.category || initialCategory);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (title.trim().length < 4 || saving) return;
    setSaving(true);
    try {
      const response = editTopic
        ? await axios.patch(`/news/forum/topics/${editTopic.id}`, { title, body, category })
        : await axios.post("/news/forum/topics", { title, body, category });
      onCreated(response.data.topic);
    } catch (error) {
      notification.error({ message: t("Comunidad", "Community"), description: errorText(error, editTopic ? t("No se pudo guardar el tema.", "Could not save the topic.") : t("No se pudo crear el tema.", "Could not create the topic.")) });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal onClose={onClose}>
      <div className="xnw-form">
        <h2>{editTopic ? t("Editar tema", "Edit topic") : t("Nuevo tema", "New topic")}</h2>
        <label>
          {t("Título", "Title")}
          <input value={title} maxLength={140} onChange={(event) => setTitle(event.target.value)} />
        </label>
        <label>
          {t("Sección", "Section")}
          <select value={category} onChange={(event) => setCategory(event.target.value)}>
            {FORUM_CATEGORIES.filter((entry) => entry.id).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.icon} {t(entry.es, entry.en)}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t("Mensaje", "Message")}
          <textarea rows={6} value={body} maxLength={4000} onChange={(event) => setBody(event.target.value)} placeholder={t("Cuéntanos más…", "Tell us more…")} />
        </label>
        <div className="xnw-actions">
          <button type="button" className="xnw-btn" onClick={onClose}>
            {t("Cancelar", "Cancel")}
          </button>
          <button type="button" className="xnw-btn is-go" disabled={title.trim().length < 4 || saving} onClick={save}>
            {editTopic ? t("Guardar cambios", "Save changes") : t("Publicar tema", "Post topic")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const TopicView = ({ ctx, topicId, onBack}) => {
  const { t, timeAgo, toggleLike } = ctx;
  const [state, setState] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [editingTopic, setEditingTopic] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState("");

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`/news/forum/topics/${topicId}`);
      setState(response.data);
    } catch (error) {
      setState({ error: errorText(error, t("No se pudo abrir el tema.", "Could not open the topic.")) });
    }
  }, [t, topicId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!state) return <div className="xnw-card xnw-empty">{t("Cargando…", "Loading…")}</div>;
  if (state.error) {
    return (
      <div className="xnw-card xnw-empty">
        {state.error}{" "}
        <button type="button" className="xnw-link" onClick={onBack}>
          {t("← Volver a los temas", "← Back to topics")}
        </button>
      </div>
    );
  }
  const { topic, replies } = state;
  const update = async (changes) => {
    try {
      const response = await axios.patch(`/news/forum/topics/${topicId}`, changes);
      setState((current) => ({ ...current, topic: response.data.topic }));
    } catch (error) {
      notification.error({ message: t("Comunidad", "Community"), description: errorText(error, t("No se pudo guardar.", "Could not save.")) });
    }
  };
  const remove = async () => {
    if (!window.confirm(t("¿Borrar este tema y sus respuestas?", "Delete this topic and its replies?"))) return;
    try {
      await axios.delete(`/news/forum/topics/${topicId}`);
      onBack();
    } catch (error) {
      notification.error({ message: t("Comunidad", "Community"), description: errorText(error, t("No se pudo borrar.", "Could not delete.")) });
    }
  };
  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const response = await axios.post("/news/comments", { target: `topic:${topicId}`, text });
      setState((current) => ({
        ...current,
        replies: [...current.replies, response.data.comment],
        topic: { ...current.topic, replyCount: current.topic.replyCount + 1 },
      }));
      setText("");
    } catch (error) {
      notification.error({ message: t("Comunidad", "Community"), description: errorText(error, t("No se pudo enviar.", "Could not send.")) });
    } finally {
      setSending(false);
    }
  };
  const removeReply = async (reply) => {
    if (!window.confirm(t("¿Borrar esta respuesta?", "Delete this reply?"))) return;
    try {
      await axios.delete(`/news/comments/${reply.id}`);
      setState((current) => ({
        ...current,
        replies: current.replies.filter((entry) => entry.id !== reply.id),
        topic: { ...current.topic, replyCount: Math.max(0, current.topic.replyCount - 1) },
      }));
    } catch (error) {
      notification.error({ message: t("Comunidad", "Community"), description: errorText(error, t("No se pudo borrar.", "Could not delete.")) });
    }
  };
  const likeTopic = () =>
    toggleLike(`topic:${topicId}`, ({ liked, count }) =>
      setState((current) => ({ ...current, topic: { ...current.topic, liked, likeCount: count } }))
    );
  const likeReply = (reply) =>
    toggleLike(`comment:${reply.id}`, ({ liked, count }) =>
      setState((current) => ({
        ...current,
        replies: current.replies.map((entry) => (entry.id === reply.id ? { ...entry, liked, likes: count } : entry)),
      }))
    );
  const canModerate = state.isAdmin;

  return (
    <div className="xnw-topic-view">
      <button type="button" className="xnw-link" onClick={onBack}>
        {t("← Volver a los temas", "← Back to topics")}
      </button>
      <div className="xnw-card xnw-topic-head">
        <div className="xnw-detail-pills">
          <span className="xnw-pill is-cyan">
            {FORUM_CATEGORIES.find((entry) => entry.id === topic.category)?.icon}{" "}
            {t(
              FORUM_CATEGORIES.find((entry) => entry.id === topic.category)?.es || "Charla general",
              FORUM_CATEGORIES.find((entry) => entry.id === topic.category)?.en || "General chat"
            ).toUpperCase()}
          </span>
          {topic.pinned ? <span className="xnw-pill is-red">📌 {t("FIJADO", "PINNED")}</span> : null}
          {topic.resolved ? <span className="xnw-pill is-green">✓ {t("RESUELTO", "SOLVED")}</span> : null}
        </div>
        <h2>{topic.title}</h2>
        <div className="xnw-comment">
          <Avatar name={topic.authorName} />
          <div className="xnw-bubble">
            <b className={topic.authorIsAdmin ? "is-admin" : ""}>
              {topic.authorName}
              {topic.authorIsAdmin ? " (Admin)" : ""}
            </b>
            {topic.body ? <div>{topic.body}</div> : null}
            <small>
              {timeAgo(topic.createdAt)}
              {topic.editedAt ? ` · ${t("editado", "edited")}` : ""}
            </small>
          </div>
        </div>
        <div className="xnw-actions">
          <button type="button" className={`xnw-btn${topic.liked ? " is-liked" : ""}`} onClick={likeTopic}>
            👍 {topic.liked ? t("Te gusta", "Liked") : t("Me gusta", "Like")} · {topic.likeCount}
          </button>
          {topic.mine || canModerate ? (
            <button type="button" className="xnw-btn" onClick={() => update({ resolved: !topic.resolved })}>
              ✓ {topic.resolved ? t("Quitar «resuelto»", "Mark unsolved") : t("Marcar resuelto", "Mark solved")}
            </button>
          ) : null}
          {canModerate ? (
            <button type="button" className="xnw-btn" onClick={() => update({ pinned: !topic.pinned })}>
              📌 {topic.pinned ? t("Desfijar", "Unpin") : t("Fijar arriba", "Pin")}
            </button>
          ) : null}
          {topic.mine || canModerate ? (
            <button type="button" className="xnw-btn" onClick={() => setEditingTopic(true)}>
              ✏️ {t("Editar tema", "Edit topic")}
            </button>
          ) : null}
          {topic.mine || canModerate ? (
            <button type="button" className="xnw-btn is-red" onClick={remove}>
              🗑 {t("Borrar tema", "Delete topic")}
            </button>
          ) : null}
        </div>
      </div>
      <div className="xnw-card xnw-replies">
        <div className="xnw-ct">💬 {replies.length === 1 ? t("1 respuesta", "1 reply") : t(`${replies.length} respuestas`, `${replies.length} replies`)}</div>
        {replies.length ? (
          replies.map((reply) => (
            <div key={reply.id} className="xnw-comment">
              <Avatar name={reply.authorName} small />
              <div className="xnw-bubble">
                <b className={reply.authorIsAdmin ? "is-admin" : ""}>
                  {reply.authorName}
                  {reply.authorIsAdmin ? " (Admin)" : ""}
                </b>
                {editingReplyId === reply.id ? (
                  <CommentEditor
                    ctx={ctx}
                    comment={reply}
                    onCancel={() => setEditingReplyId("")}
                    onSaved={(saved) => {
                      setEditingReplyId("");
                      setState((current) => ({
                        ...current,
                        replies: current.replies.map((entry) => (entry.id === saved.id ? { ...entry, text: saved.text, editedAt: saved.editedAt } : entry)),
                      }));
                    }}
                  />
                ) : (
                  <div>{reply.text}</div>
                )}
                <small>
                  {timeAgo(reply.createdAt)}
                  {reply.editedAt ? ` · ${t("editado", "edited")}` : ""}
                  <button type="button" className={`xnw-link${reply.liked ? " is-on" : ""}`} onClick={() => likeReply(reply)}>
                    👍 {reply.likes}
                  </button>
                  {reply.canEdit && editingReplyId !== reply.id ? (
                    <button type="button" className="xnw-link" onClick={() => setEditingReplyId(reply.id)}>
                      {t("Editar", "Edit")}
                    </button>
                  ) : null}
                  {reply.canDelete ? (
                    <button type="button" className="xnw-link is-red" onClick={() => removeReply(reply)}>
                      {t("Borrar", "Delete")}
                    </button>
                  ) : null}
                </small>
              </div>
            </div>
          ))
        ) : (
          <div className="xnw-muted">{t("Nadie ha respondido todavía.", "No replies yet.")}</div>
        )}
        <div className="xnw-compose">
          <textarea value={text} maxLength={2000} onChange={(event) => setText(event.target.value)} placeholder={t("Escribe tu respuesta…", "Write your reply…")} />
          <button type="button" className="xnw-btn is-go" disabled={!text.trim() || sending} onClick={send}>
            {t("Responder ➤", "Reply ➤")}
          </button>
        </div>
      </div>
      {editingTopic ? (
        <TopicComposer
          ctx={ctx}
          editTopic={topic}
          onClose={() => setEditingTopic(false)}
          onCreated={(saved) => {
            setEditingTopic(false);
            setState((current) => ({ ...current, topic: saved }));
          }}
        />
      ) : null}
    </div>
  );
};

const Roadmap = ({ ctx }) => {
  const { t, timeAgo, loadFeed, isAdmin, roadmap } = ctx;
  const [title, setTitle] = useState("");
  const [status, setStatus] = useState("doing");
  const save = async (request) => {
    try {
      await request();
      loadFeed();
    } catch (error) {
      notification.error({ message: t("En qué trabajamos", "Roadmap"), description: errorText(error, t("No se pudo guardar.", "Could not save.")) });
    }
  };
  const add = () => {
    if (!title.trim()) return;
    save(() => axios.post("/news/roadmap", { title, status, progress: status === "done" ? 100 : 10 }));
    setTitle("");
  };
  return (
    <div className="xnw-roadmap">
      <p className="xnw-muted xnw-lead">
        {t(
          "Aquí contamos en qué estamos trabajando, lo que viene después y lo que ya terminamos.",
          "What we're working on now, what comes next and what's already done."
        )}
      </p>
      {isAdmin ? (
        <div className="xnw-card xnw-new">
          <input value={title} maxLength={120} onChange={(event) => setTitle(event.target.value)} placeholder={t("Nueva tarea, p. ej.: Partidas por turnos", "New task")} />
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            {ROADMAP_COLUMNS.map((column) => (
              <option key={column.id} value={column.id}>
                {t(column.es, column.en)}
              </option>
            ))}
          </select>
          <button type="button" className="xnw-btn is-go" onClick={add} disabled={!title.trim()}>
            ＋ {t("Añadir", "Add")}
          </button>
        </div>
      ) : null}
      <div className="xnw-kanban">
        {ROADMAP_COLUMNS.map((column) => {
          const entries = roadmap.filter((entry) => entry.status === column.id);
          return (
            <div key={column.id} className="xnw-card xnw-kcol">
              <div className={`xnw-kcol-head is-${column.tone}`}>
                {t(column.es, column.en)} <span>{entries.length}</span>
              </div>
              {entries.length ? (
                entries.map((entry) => (
                  <div key={entry.id} className="xnw-ktask">
                    <b>{entry.title}</b>
                    {column.id !== "done" ? (
                      <div className="xnw-bar">
                        <i style={{ width: `${entry.progress}%` }} />
                      </div>
                    ) : null}
                    <small>
                      {column.id !== "done" ? `${entry.progress}% · ` : ""}
                      {t("actualizado", "updated")} {timeAgo(entry.updatedAt)}
                    </small>
                    {isAdmin ? (
                      <div className="xnw-ktools">
                        <select
                          value={entry.status}
                          onChange={(event) => save(() => axios.patch(`/news/roadmap/${entry.id}`, { status: event.target.value }))}
                        >
                          {ROADMAP_COLUMNS.map((option) => (
                            <option key={option.id} value={option.id}>
                              {t(option.es, option.en)}
                            </option>
                          ))}
                        </select>
                        {entry.status !== "done" ? (
                          <select
                            value={Math.round(entry.progress / 10) * 10}
                            onChange={(event) => save(() => axios.patch(`/news/roadmap/${entry.id}`, { progress: Number(event.target.value) }))}
                          >
                            {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90].map((value) => (
                              <option key={value} value={value}>
                                {value}%
                              </option>
                            ))}
                          </select>
                        ) : null}
                        <button
                          type="button"
                          className="xnw-link is-red"
                          onClick={() =>
                            window.confirm(t("¿Borrar esta tarea?", "Delete this task?")) &&
                            save(() => axios.delete(`/news/roadmap/${entry.id}`))
                          }
                        >
                          {t("Borrar", "Delete")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))
              ) : (
                <div className="xnw-muted">{t("Nada por ahora.", "Nothing yet.")}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const News = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext() || {};
  const language = normalizeLanguage(outletContext.language || "es");
  const routeActions = useModernRouteActions();
  const t = useCallback((es, en) => translatePair(language, es, en), [language]);
  const locale = language === "es" ? "es-ES" : "en-US";

  const queryTab = new URLSearchParams(location.search).get("tab");
  const tab = TABS.includes(queryTab) ? queryTab : "news";
  const setTab = useCallback(
    (nextTab, extra = "") => navigate(`/game/news${nextTab === "news" ? "" : `?tab=${nextTab}`}${extra}`, { replace: true }),
    [navigate]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [filter, setFilter] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [notifyOn, setNotifyOn] = useState(readNotify);
  const [openItemKey, setOpenItemKey] = useState("");
  const [editor, setEditor] = useState(null);

  const isAdmin = Boolean(data?.isAdmin);

  useEffect(() => {
    document.body.classList.add("xima-lobby-modern-active", "xnw-page-active");
    return () => document.body.classList.remove("xima-lobby-modern-active", "xnw-page-active");
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 30000);
    return () => window.clearInterval(timer);
  }, []);

  const loadFeed = useCallback(async () => {
    try {
      setLoadError("");
      const response = await axios.get("/news/feed");
      setData(response?.data || null);
    } catch (error) {
      setLoadError(errorText(error, t("No se pudieron cargar las noticias.", "Could not load the news.")));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadFeed().catch(() => {});
  }, [loadFeed]);

  // Al abrir Noticias se apaga la bolita roja del menú.
  useEffect(() => {
    outletContext.markSidebarBadgeSeen?.("news");
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeAgo = useCallback(
    (value) => {
      const ms = nowMs - new Date(value || 0).valueOf();
      if (!value || !Number.isFinite(ms)) return "";
      const min = Math.max(0, Math.floor(ms / 60000));
      if (min < 1) return t("hace un momento", "just now");
      if (min < 60) return t(`hace ${min} min`, `${min} min ago`);
      const hours = Math.floor(min / 60);
      if (hours < 24) return t(`hace ${hours} ${hours === 1 ? "hora" : "horas"}`, `${hours}h ago`);
      const days = Math.floor(hours / 24);
      if (days === 1) return t("ayer", "yesterday");
      if (days < 30) return t(`hace ${days} días`, `${days} days ago`);
      return new Date(value).toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
    },
    [locale, nowMs, t]
  );

  const formatDateTime = useCallback(
    (value) =>
      new Date(value).toLocaleString(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "numeric",
        minute: "2-digit",
      }),
    [locale]
  );

  const items = useMemo(() => data?.items || [], [data]);
  const featuredItem = useMemo(() => {
    if (data?.featured?.type !== "post") return null;
    return items.find((item) => item.key === data.featured.key) || null;
  }, [data, items]);
  const championship = data?.championship || null;
  const showChampionshipHero = data?.featured?.type === "championship" && championship;

  const autoText = useCallback(
    (item) => {
      if (item.text) return item.text;
      const meta = item.meta || {};
      if (item.category === "torneo") {
        const parts = [
          meta.mode,
          t(`${meta.players} jugadores`, `${meta.players} players`),
          t(`turno de ${meta.turnMinutes} min`, `${meta.turnMinutes} min turns`),
        ].filter(Boolean);
        if (meta.startAt) parts.push(`${t("empieza", "starts")} ${formatDateTime(meta.startAt)}`);
        return parts.join(" · ");
      }
      return "";
    },
    [formatDateTime, t]
  );

  const listItems = useMemo(
    () =>
      items.filter(
        (item) =>
          (!filter || item.category === filter || (filter === "torneo" && item.category === "campeonato")) &&
          (showChampionshipHero || item.key !== featuredItem?.key || filter)
      ),
    [featuredItem, filter, items, showChampionshipHero]
  );

  const patchItem = useCallback((key, changes) => {
    setData((current) =>
      current ? { ...current, items: current.items.map((item) => (item.key === key ? { ...item, ...changes } : item)) } : current
    );
  }, []);

  const toggleLike = useCallback(
    async (target, onDone) => {
      try {
        const response = await axios.post("/news/like", { target });
        onDone?.(response.data);
      } catch (error) {
        notification.error({ message: t("Me gusta", "Like"), description: errorText(error, t("No se pudo guardar.", "Could not save.")) });
      }
    },
    [t]
  );

  const likeItem = useCallback(
    (item) => toggleLike(item.key, ({ liked, count }) => patchItem(item.key, { liked, likes: count })),
    [patchItem, toggleLike]
  );

  const toggleNotify = useCallback(async () => {
    const next = !notifyOn;
    if (next && typeof window !== "undefined" && "Notification" in window && window.Notification.permission !== "granted") {
      try {
        await window.Notification.requestPermission();
      } catch {
        // Algunos navegadores no preguntan: quedan los avisos dentro de Xima.
      }
    }
    try {
      window.localStorage.setItem(NEWS_NOTIFY_STORAGE_KEY, next ? "1" : "0");
    } catch {
      // Sin almacenamiento el aviso dura solo esta visita.
    }
    setNotifyOn(next);
    const canNotify = typeof window !== "undefined" && "Notification" in window && window.Notification.permission === "granted";
    notification.success({
      message: next ? t("Avisos activados", "Alerts on") : t("Avisos desactivados", "Alerts off"),
      description: next
        ? canNotify
          ? t("Te avisaremos en este navegador cuando haya una noticia nueva.", "We'll alert you in this browser when there is news.")
          : t("Tu navegador no permite avisos; verás el número rojo en Noticias.", "Your browser blocks alerts; you'll see the red counter on News.")
        : t("Ya no te avisaremos de las noticias nuevas.", "We won't alert you about new posts."),
    });
  }, [notifyOn, t]);

  const goTo = useCallback(
    (link) => {
      if (!link) return;
      if (/^https:\/\//i.test(link)) {
        window.open(link, "_blank", "noopener,noreferrer");
        return;
      }
      navigate(link);
    },
    [navigate]
  );

  const openItem = items.find((item) => item.key === openItemKey) || null;

  const renderCategoryPill = (category) => {
    const meta = CATEGORIES[category] || CATEGORIES.novedad;
    return (
      <span className={`xnw-pill is-${meta.tone}`}>
        {meta.icon} {t(meta.es, meta.en).toUpperCase()}
      </span>
    );
  };

  // ---------- Pestaña Noticias ----------
  const renderHero = () => {
    if (showChampionshipHero) {
      const startText = championship.startAt
        ? `${t("Empieza el", "Starts on")} ${formatDateTime(championship.startAt)}.`
        : t("La fecha se anunciará pronto.", "Date coming soon.");
      const running = championship.status === "running";
      return (
        <div className="xnw-card xnw-hero">
          <div className="xnw-hero-img is-gold">
            <span className="xnw-pill is-gold xnw-hero-flag">⭐ {t("DESTACADA", "FEATURED")}</span>
            🏆
          </div>
          <div className="xnw-hero-txt">
            {renderCategoryPill("campeonato")}
            <h2>
              {championship.title}: {running ? t("¡en juego ahora!", "live now!") : t("inscripciones abiertas", "registration open")}
            </h2>
            <p>
              {[
                t(`${championship.maxPlayers} jugadores`, `${championship.maxPlayers} players`),
                MODE_NAMES[championship.gameMode] || "",
                championship.timeMinutes ? t(`turno de ${championship.timeMinutes} min`, `${championship.timeMinutes} min turns`) : "",
                championship.prize ? `${t("premio", "prize")}: ${championship.prize}` : "",
              ]
                .filter(Boolean)
                .join(" · ")}
              . {running ? "" : startText}
            </p>
            <div className="xnw-actions">
              <button type="button" className="xnw-btn is-go" onClick={() => navigate("/game/championship")}>
                {running ? t("Ver en vivo", "Watch live") : t("Inscribirme", "Sign up")}
              </button>
              <button type="button" className="xnw-btn" onClick={() => navigate("/game/championship")}>
                {t("Leer más", "Read more")}
              </button>
            </div>
            <div className="xnw-foot">
              <span>{t(`${championship.players} de ${championship.maxPlayers} inscritos`, `${championship.players} of ${championship.maxPlayers} signed up`)}</span>
            </div>
          </div>
        </div>
      );
    }
    if (!featuredItem) return null;
    const meta = CATEGORIES[featuredItem.category] || CATEGORIES.novedad;
    return (
      <div className="xnw-card xnw-hero">
        <div className={`xnw-hero-img is-${meta.tone}`}>
          <span className="xnw-pill is-gold xnw-hero-flag">⭐ {t("DESTACADA", "FEATURED")}</span>
          {iconFor(featuredItem)}
        </div>
        <div className="xnw-hero-txt">
          {renderCategoryPill(featuredItem.category)}
          <h2>{featuredItem.title}</h2>
          <p className="xnw-clamp3">{autoText(featuredItem)}</p>
          <div className="xnw-actions">
            {featuredItem.ctaLink ? (
              <button type="button" className="xnw-btn is-go" onClick={() => goTo(featuredItem.ctaLink)}>
                {featuredItem.ctaLabel || t("Ver", "Open")}
              </button>
            ) : null}
            <button type="button" className="xnw-btn" onClick={() => setOpenItemKey(featuredItem.key)}>
              {t("Leer más", "Read more")}
            </button>
          </div>
          <div className="xnw-foot">
            <span>
              {featuredItem.author} · {timeAgo(featuredItem.createdAt)}
            </span>
            <span className="xnw-foot-actions">
              <button type="button" className={`xnw-mini${featuredItem.liked ? " is-on" : ""}`} onClick={() => likeItem(featuredItem)}>
                👍 {featuredItem.likes}
              </button>
              <button type="button" className="xnw-mini" onClick={() => setOpenItemKey(featuredItem.key)}>
                💬 {featuredItem.comments}
              </button>
            </span>
          </div>
        </div>
      </div>
    );
  };

  // El cronómetro sale siempre; sin hora fijada (o ya empezado) marca 00 00 00.
  const renderCountdown = () => {
    const startMs = championship?.startAt ? new Date(championship.startAt).valueOf() : 0;
    const remaining = championship?.status === "registration" ? Math.max(0, startMs - nowMs) : 0;
    const percent = championship?.maxPlayers ? Math.min(100, Math.round((championship.players / championship.maxPlayers) * 100)) : 0;
    let note = "";
    if (!championship) note = t("Pronto anunciaremos el próximo campeonato.", "The next championship is coming soon.");
    else if (championship.status === "running") note = "";
    else if (championship.status === "finished") note = t("Terminó. Pronto anunciaremos el próximo.", "Finished. The next one is coming soon.");
    else if (!startMs) note = t("Fecha por anunciar · inscripciones abiertas", "Date to be announced · sign-up open");
    else if (remaining <= 0) note = t("¡Está a punto de empezar!", "About to start!");
    return (
      <>
        <div className="xnw-muted">{championship?.title || t("Campeonato XIMA", "XIMA Championship")}</div>
        <div className="xnw-countdown">
          {[
            [Math.floor(remaining / 864e5), t("DÍAS", "DAYS")],
            [Math.floor((remaining % 864e5) / 36e5), t("HORAS", "HOURS")],
            [Math.floor((remaining % 36e5) / 6e4), "MIN"],
          ].map(([value, label]) => (
            <div key={label}>
              <b>{String(value).padStart(2, "0")}</b>
              <span>{label}</span>
            </div>
          ))}
        </div>
        {championship?.status === "running" ? <div className="xnw-live xnw-cd-note">● {t("En juego ahora", "Live now")}</div> : null}
        {note ? <div className="xnw-muted xnw-cd-note">{note}</div> : null}
        {championship && championship.status !== "finished" ? (
          <>
            <div className="xnw-muted">
              {t(`${championship.players} de ${championship.maxPlayers} inscritos`, `${championship.players} of ${championship.maxPlayers} signed up`)}
            </div>
            <div className="xnw-bar">
              <i style={{ width: `${percent}%` }} />
            </div>
          </>
        ) : null}
      </>
    );
  };

  const roadmap = data?.roadmap || [];
  const roadmapPill = (status) => {
    const column = ROADMAP_COLUMNS.find((entry) => entry.id === status) || ROADMAP_COLUMNS[1];
    return <span className={`xnw-pill is-${column.tone}`}>{t(column.es, column.en)}</span>;
  };

  const renderCard = (item) => {
    const meta = CATEGORIES[item.category] || CATEGORIES.novedad;
    return (
      <article key={item.key} className="xnw-card xnw-nc" onClick={() => setOpenItemKey(item.key)}>
        <div className={`xnw-thumb is-${meta.tone}`}>
          {iconFor(item)}
          {item.pinned ? <span className="xnw-pin">📌</span> : null}
        </div>
        <div className="xnw-nc-body">
          {renderCategoryPill(item.category)}
          <h3>{item.title}</h3>
          <div className="xnw-muted xnw-clamp2">{autoText(item)}</div>
          <div className="xnw-foot">
            <span>{timeAgo(item.createdAt)}</span>
            <span className="xnw-foot-actions">
              <button
                type="button"
                className={`xnw-mini${item.liked ? " is-on" : ""}`}
                onClick={(event) => {
                  event.stopPropagation();
                  likeItem(item);
                }}
              >
                👍 {item.likes}
              </button>
              {item.comments ? <span className="xnw-mini is-static">💬 {item.comments}</span> : null}
            </span>
          </div>
        </div>
      </article>
    );
  };

  const renderNewsTab = () => (
    <div className="xnw-grid">
      <div className="xnw-col-main">
        {renderHero()}
        <div className="xnw-chips">
          {FILTERS.map((entry) => (
            <button
              key={entry.id || "all"}
              type="button"
              className={`xnw-chip${filter === entry.id ? " is-on" : ""}`}
              onClick={() => setFilter(entry.id)}
            >
              {t(entry.es, entry.en)}
            </button>
          ))}
        </div>
        {listItems.length ? (
          filter ? (
            <div className="xnw-cards">{listItems.map(renderCard)}</div>
          ) : (
            <>
              <div className="xnw-cards xnw-cols3">
                {splitInColumns(listItems).map((column, index) => (
                  <div key={index} className="xnw-col">
                    {column.map(renderCard)}
                  </div>
                ))}
              </div>
              <div className="xnw-cards xnw-cols-mobile">{listItems.map(renderCard)}</div>
            </>
          )
        ) : (
          <div className="xnw-card xnw-empty">{t("No hay noticias en esta sección todavía.", "No news here yet.")}</div>
        )}
      </div>
      <aside className="xnw-col-side">
        <button type="button" className="xnw-card xnw-side-card" onClick={() => navigate("/game/championship")}>
          <div className="xnw-ct">🥇 {t("Próximo campeonato", "Next championship")}</div>
          {renderCountdown()}
        </button>
        <div className="xnw-card xnw-side-card">
          <div className="xnw-ct">
            🛠️ {t("En qué trabajamos", "What we're working on")}
            <button type="button" className="xnw-link" onClick={() => setTab("roadmap")}>
              {t("Ver todo →", "See all →")}
            </button>
          </div>
          {roadmap.length ? (
            roadmap.slice(0, 4).map((entry) => (
              <div key={entry.id} className="xnw-rm">
                {roadmapPill(entry.status)}
                <div className="xnw-rm-title">
                  {entry.title}
                  {entry.status === "doing" ? (
                    <div className="xnw-bar">
                      <i style={{ width: `${entry.progress}%` }} />
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          ) : (
            <div className="xnw-muted">{t("Pronto contaremos aquí lo que viene.", "Coming soon.")}</div>
          )}
        </div>
        <div className="xnw-card xnw-side-card">
          <div className="xnw-ct">
            💬 {t("Comunidad", "Community")}
            <button type="button" className="xnw-link" onClick={() => setTab("community")}>
              {t("Ver todo →", "See all →")}
            </button>
          </div>
          {(data?.community?.latest || []).length ? (
            data.community.latest.slice(0, 3).map((topic) => (
              <button key={topic.id} type="button" className="xnw-dz" onClick={() => setTab("community", `&topic=${topic.id}`)}>
                <Avatar name={topic.authorName} small />
                <span>
                  <b>{topic.title}</b>
                  <small>
                    {topic.authorName} · {topic.replyCount === 1 ? t("1 respuesta", "1 reply") : t(`${topic.replyCount} respuestas`, `${topic.replyCount} replies`)}
                  </small>
                </span>
              </button>
            ))
          ) : (
            <button type="button" className="xnw-dz" onClick={() => setTab("community")}>
              <span>
                <b>{t("Abre el primer tema del foro", "Start the first forum topic")}</b>
                <small>{t("Pregunta, comparte estrategias o propone ideas.", "Ask, share strategies or suggest ideas.")}</small>
              </span>
            </button>
          )}
        </div>
      </aside>
    </div>
  );

  // ---------- Detalle de una noticia con comentarios ----------

  // ---------- Publicar o editar una noticia (admin) ----------

  // ---------- Pestaña Comunidad (foro) ----------



  // ---------- Pestaña En qué trabajamos ----------

  // ---------- Pestaña Calendario ----------
  const renderCalendar = () => {
    const calendar = data?.calendar || [];
    return (
      <div className="xnw-calendar">
        <p className="xnw-muted xnw-lead">
          {t("Torneos y campeonatos programados. Pulsa uno para ir a él.", "Scheduled tournaments and championships. Tap one to open it.")}
        </p>
        {calendar.length ? (
          <div className="xnw-card xnw-cal-list">
            {calendar.map((entry) => {
              const date = new Date(entry.startAt);
              return (
                <button key={`${entry.type}-${entry.id}`} type="button" className="xnw-cal" onClick={() => navigate(entry.link)}>
                  <span className="xnw-cal-day">
                    <b>{date.getDate()}</b>
                    <small>{date.toLocaleDateString(locale, { month: "short" }).replace(".", "").toUpperCase()}</small>
                  </span>
                  <span className="xnw-cal-txt">
                    <span>
                      {entry.type === "championship" ? <span className="xnw-pill is-gold">🥇 {t("CAMPEONATO", "CHAMPIONSHIP")}</span> : null}
                      <b>{entry.title}</b>
                    </span>
                    <small>
                      {date.toLocaleString(locale, { weekday: "long", hour: "numeric", minute: "2-digit" })}
                      {entry.mode ? ` · ${entry.mode}` : ""} · {t(`${entry.players} jugadores`, `${entry.players} players`)}
                      {entry.turnMinutes ? ` · ${t(`turno de ${entry.turnMinutes} min`, `${entry.turnMinutes} min turns`)}` : ""}
                    </small>
                  </span>
                  <span className="xnw-btn">{t("Ver →", "Open →")}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="xnw-card xnw-empty">
            {t("No hay torneos programados ahora mismo.", "No tournaments scheduled right now.")}{" "}
            <button type="button" className="xnw-link" onClick={() => navigate("/game/play")}>
              {t("Ver todos los torneos →", "See all tournaments →")}
            </button>
          </div>
        )}
      </div>
    );
  };

  const ctx = { t, locale, timeAgo, patchItem, loadFeed, setOpenItemKey, setEditor, goTo, likeItem, isAdmin, autoText, renderCategoryPill, location, setTab, toggleLike, roadmap };

  const tabButtons = [
    ["news", "📰", t("Noticias", "News")],
    ["community", "💬", t("Comunidad", "Community")],
    ["roadmap", "🛠️", t("En qué trabajamos", "What we're working on")],
    ["calendar", "📅", t("Calendario", "Calendar")],
  ];

  return (
    <div className="xlm-root xnw-root">
      <ModernSidebar
        language={language}
        active="news"
        badges={outletContext.sidebarBadges || {}}
        isAdmin={Boolean(outletContext.hasAdminMenuAccess)}
        actions={routeActions}
      />
      <div className="xlm-main xnw-main">
        <button type="button" className="xnw-back" onClick={() => navigate("/game/lobby")}>
          ← {t("Volver al lobby", "Back to lobby")}
        </button>
        <div className="xnw-head">
          <div className="xnw-ico">{tab === "community" ? "💬" : "📰"}</div>
          <div className="xnw-head-txt">
            <h1>{tab === "community" ? t("COMUNIDAD", "COMMUNITY") : t("NOTICIAS", "NEWS")}</h1>
            <p>
              {tab === "community"
                ? t("Foro de Xima: pregunta, comparte estrategias y propone ideas.", "Xima forum: ask, share strategies and suggest ideas.")
                : t("Todo lo nuevo de Xima: novedades, puzzles, torneos, eventos y lo que viene.", "Everything new in Xima: news, puzzles, tournaments, events and what's next.")}
            </p>
          </div>
          <div className="xnw-head-actions">
            <button type="button" className={`xnw-btn${notifyOn ? " is-liked" : ""}`} onClick={toggleNotify}>
              🔔 {notifyOn ? t("Avisos activados", "Alerts on") : t("Avisarme", "Alert me")}
            </button>
            {isAdmin ? (
              <button type="button" className="xnw-btn is-go" onClick={() => setEditor({})}>
                ＋ {t("Publicar noticia", "Publish news")}
              </button>
            ) : null}
          </div>
        </div>
        <div className="xnw-tabs">
          {tabButtons.map(([id, icon, label]) => (
            <button key={id} type="button" className={`xnw-tab${tab === id ? " is-on" : ""}`} onClick={() => setTab(id)}>
              {icon} {label}
            </button>
          ))}
        </div>
        {loading && !data ? (
          <div className="xnw-card xnw-empty">{t("Cargando noticias…", "Loading news…")}</div>
        ) : loadError && !data ? (
          <div className="xnw-card xnw-empty">
            {loadError}{" "}
            <button type="button" className="xnw-link" onClick={() => loadFeed()}>
              {t("Reintentar", "Retry")}
            </button>
          </div>
        ) : tab === "community" ? (
          <Community ctx={ctx} />
        ) : tab === "roadmap" ? (
          <Roadmap ctx={ctx} />
        ) : tab === "calendar" ? (
          renderCalendar()
        ) : (
          renderNewsTab()
        )}
        {openItem ? <ItemDetail ctx={ctx} item={openItem} /> : null}
        {editor ? <PostEditor ctx={ctx} initial={editor} /> : null}
      </div>
    </div>
  );
};

export default News;
